import { useI18n } from '../../../i18n/I18nProvider';
import type {
  ApprovalRoute,
  AuditEntry,
  ConfigPackage,
  MergePreview,
  RoleCode,
  UserRecord,
} from '../settings.api';

export const ROLES: readonly RoleCode[] = ['admin', 'chief_accountant', 'accountant'];

export function ApprovalRoutesView({
  routes,
  error,
}: {
  routes: readonly ApprovalRoute[];
  error?: string | null;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-approval-routes">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {routes.length === 0 ? (
        <p className="muted">{t('settings.empty')}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('settings.approvals.documentType')}</th>
              <th>{t('settings.approvals.steps')}</th>
              <th>{t('settings.approvals.deadline')}</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((route) => (
              <tr key={route.id} data-testid="approval-route">
                <td>{route.documentType}</td>
                <td>
                  {route.steps
                    .map((step) => step.role ?? step.userId ?? '')
                    .filter((value) => value !== '')
                    .join(' → ')}
                </td>
                <td className="mono">
                  {route.steps
                    .map((step) => step.deadlineHours)
                    .filter((value): value is number => typeof value === 'number')
                    .map((value) => `${String(value)}h`)
                    .join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function UsersView({ users, error }: { users: readonly UserRecord[]; error?: string | null }): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-users">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      <table className="table">
        <thead>
          <tr>
            <th>{t('settings.users.email')}</th>
            <th>{t('settings.users.fullName')}</th>
            {ROLES.map((role) => (
              <th key={role} data-role={role}>
                {t(`settings.role.${role}`)}
              </th>
            ))}
            <th>{t('settings.users.state')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} data-testid="user-row" data-user={user.id}>
              <td className="mono">{user.email}</td>
              <td>{user.fullName}</td>
              {ROLES.map((role) => {
                const has = user.roles.includes(role);
                return (
                  <td key={role} data-has-role={has ? 'true' : 'false'} data-role-cell={role}>
                    {has ? t('settings.users.yes') : t('settings.users.no')}
                  </td>
                );
              })}
              <td>{user.isActive ? t('settings.users.active') : t('settings.users.blocked')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PackagesView({
  package: pkg,
  preview,
  error,
}: {
  package: ConfigPackage | null;
  preview?: MergePreview | null;
  error?: string | null;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-packages">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {pkg === null ? (
        <p className="muted">{t('settings.empty')}</p>
      ) : (
        <article className="settings__card" data-testid="package">
          <header className="settings__card-head">
            <strong>{pkg.name}</strong>
            <span className="pill">{pkg.format}</span>
          </header>
          <p className="muted">
            {t('settings.packages.generatedAt')}: <span className="mono">{pkg.generatedAt}</span>
          </p>
          <p className="muted">
            {t('settings.packages.settingsCount')}: <strong>{String(pkg.settings.length)}</strong>
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>{t('settings.packages.key')}</th>
                <th>{t('settings.packages.scope')}</th>
                <th>{t('settings.packages.value')}</th>
                <th>{t('settings.packages.version')}</th>
              </tr>
            </thead>
            <tbody>
              {pkg.settings.map((setting) => (
                <tr key={`${setting.key}-${setting.scope}-${setting.scopeId ?? ''}`} data-testid="package-setting">
                  <td className="mono">{setting.key}</td>
                  <td>{setting.scope}</td>
                  <td className="mono">{JSON.stringify(setting.value)}</td>
                  <td className="mono">{String(setting.version)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      )}

      {preview === null || preview === undefined ? null : (
        <article className="settings__card" data-testid="merge-preview" data-conflicts={preview.conflicts} data-changes={preview.changes}>
          <header className="settings__card-head">
            <strong>{t('settings.packages.mergePreview')}</strong>
            <span className={preview.conflicts > 0 ? 'pill pill--warn' : 'pill pill--ok'}>
              {t('settings.packages.conflicts')}: {String(preview.conflicts)}
            </span>
          </header>
          <p className="muted">
            {t('settings.packages.changes')}: <strong>{String(preview.changes)}</strong>
          </p>
        </article>
      )}
    </div>
  );
}

export function AuditView({ entries, error }: { entries: readonly AuditEntry[]; error?: string | null }): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="settings__panel" data-testid="settings-audit">
      {error === null || error === undefined ? null : <p className="alert alert--danger">{error}</p>}
      {entries.length === 0 ? (
        <p className="muted">{t('settings.empty')}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('settings.audit.at')}</th>
              <th>{t('settings.audit.actor')}</th>
              <th>{t('settings.audit.action')}</th>
              <th>{t('settings.audit.entity')}</th>
              <th>{t('settings.audit.change')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} data-testid="audit-row">
                <td className="mono">{entry.at}</td>
                <td>{entry.actor}</td>
                <td>{entry.action}</td>
                <td className="mono">{entry.entity}</td>
                <td className="mono">
                  {entry.oldValue ?? '—'} → {entry.newValue ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
